import { CELL } from '../core/config';
import { key, MATERIALS, type Building, type Cell, type City, type Material, type CellRole } from './city';

/** Stable pre-revision centre landmarks. Heights include their original roof plant. */
export const LANDMARK_BASELINES = [
  { id: 0, height: 70, width: 22, depth: 22, name: 'Alder Exchange', design: 'diagrid' },
  { id: 1, height: 85, width: 24, depth: 22, name: 'Meridian Terraces', design: 'setbacks' },
  { id: 2, height: 66.25, width: 22, depth: 22, name: 'Beacon Crown', design: 'octagonal' },
  { id: 4, height: 58.75, width: 24, depth: 22, name: 'Juniper Skygardens', design: 'paired' },
] as const;

export function prepareTallLandmarks(city: City, multiplier = 6): City {
  if (multiplier > 1) city.initialView = { targetY: 190, distance: 700 };
  for (const baseline of LANDMARK_BASELINES) {
    const old = city.buildings.find((b) => b.id === baseline.id);
    if (!old || old.landmark) continue;
    const floors = Math.round(((baseline.height * multiplier) / CELL - 2) / 3),
      roof = floors * 3;
    const b: Building = {
      ...old,
      name: baseline.name,
      w: baseline.width,
      d: baseline.depth,
      h: roof + 2,
      roofLevel: roof,
      cells: [],
      landmark: {
        baselineHeight: baseline.height,
        multiplier,
        design: baseline.design,
        occupiedFloors: [0, 1, Math.floor(floors * 0.35), Math.floor(floors * 0.7), floors - 2],
      },
    };
    const inside = (x: number, y: number, z: number): boolean => {
      const floor = Math.floor(y / 3);
      const inset =
        baseline.design === 'setbacks'
          ? floor >= floors * 0.82
            ? 4
            : floor >= floors * 0.59
              ? 3
              : floor >= floors * 0.33
                ? 1
                : 0
          : 0;
      if (x < inset || x >= b.w - inset || z < inset || z >= b.d - inset) return false;
      if (
        baseline.design === 'octagonal' &&
        floor >= 3 &&
        Math.abs(x - (b.w - 1) / 2) + Math.abs(z - (b.d - 1) / 2) > b.w * 0.72
      )
        return false;
      if (
        baseline.design === 'paired' &&
        floor > 5 &&
        y < roof - 3 &&
        x >= 11 &&
        x < 13 &&
        ![Math.floor(floors * 0.35), Math.floor(floors * 0.7)].some((f) => floor >= f && floor <= f + 1)
      )
        return false;
      return true;
    };
    const put = (
      x: number,
      y: number,
      z: number,
      material: Material,
      role: CellRole,
      color: number,
    ): Cell => {
      const c: Cell = {
        id: b.id * 1e6 + key(x, y, z),
        x,
        y,
        z,
        material,
        role,
        color,
        hp: MATERIALS[material].strength,
        root: y === 0,
      };
      // Larger real structural members carry the taller building. Blast HP is
      // unchanged; this capacity changes self-weight support, never immunity.
      if (role === 'column' || role === 'beam') c.capacity = (material === 'metal' ? 1800 : 660) * multiplier;
      if ((role === 'slab' || role === 'roof' || (role === 'beam' && y % 3 === 0)) && y > 0)
        c.bounds = { x: 0, y: 0.75, z: 0, w: 1, h: 0.25, d: 1 };
      b.cells.push(c);
      return c;
    };
    for (let y = 0; y <= roof; y++)
      for (let z = 0; z < b.d; z++)
        for (let x = 0; x < b.w; x++) {
          if (!inside(x, y, z)) continue;
          const edge =
            !inside(x - 1, y, z) || !inside(x + 1, y, z) || !inside(x, y, z - 1) || !inside(x, y, z + 1);
          const core =
            baseline.design === 'paired'
              ? (Math.abs(x - 5) < 1.1 || Math.abs(x - 18) < 1.1) && Math.abs(z - 11) < 1.1
              : Math.abs(x - b.w / 2) < 1.1 && Math.abs(z - b.d / 2) < 1.1;
          const pier =
            (x % 4 === 0 || x === b.w - 1 || (baseline.design === 'paired' && (x === 10 || x === 13))) &&
            (z % 4 === 0 || z === b.d - 1);
          const diagonal =
            baseline.design === 'diagrid' &&
            edge &&
            ((x + z + Math.floor(y / 2)) % 12 === 0 || (x + z - Math.floor(y / 2) + 1200) % 12 === 0);
          const bridge = baseline.design === 'paired' && x >= 10 && x <= 13 && y > 15;
          const column = core || pier || diagonal,
            slab = y % 3 === 0;
          const beam = y % 3 === 2 && (x % 4 === 0 || z % 4 === 0) && !edge;
          if (!column && !slab && !beam && !edge) continue;
          const role: CellRole = column
            ? 'column'
            : bridge
              ? 'beam'
              : slab
                ? y === roof
                  ? 'roof'
                  : 'slab'
                : beam
                  ? 'beam'
                  : 'glass';
          const material: Material = column || beam || bridge ? 'metal' : slab ? 'concrete' : 'glass';
          const color =
            material === 'metal'
              ? baseline.design === 'diagrid'
                ? 0x829c9d
                : 0xc6d0c9
              : material === 'concrete'
                ? 0xd8ddd2
                : baseline.design === 'paired'
                  ? 0x6e9ea0
                  : baseline.design === 'octagonal'
                    ? 0x83b5bd
                    : 0x739fa8;
          put(x, y, z, material, role, color);
        }
    for (let x = Math.floor(b.w / 2) - 1; x <= Math.floor(b.w / 2); x++)
      for (let z = Math.floor(b.d / 2) - 1; z <= Math.floor(b.d / 2); z++)
        put(x, roof + 1, z, 'metal', 'prop', 0x9ba9a3);
    city.buildings[city.buildings.indexOf(old)] = b;
  }
  return city;
}
